import type {
    MailRibbonConfigStore,
    RibbonConfiguration,
    TabScalingAndLayoutConfig,
} from 'owa-mail-ribbon-store-shared-types';
import type { HelpRibbonTabId, MailRibbonTabId } from 'owa-ribbon-ids';
import { type LabelPreferenceId } from 'owa-mail-ribbon-store-shared-types/lib/util/labelPreferencesIds';
import { createStore } from 'owa-satcheljs';
import type { MailboxInfo, AccountKey } from 'owa-client-types';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isMessageFileMailboxInfo } from 'owa-client-types/lib/MessageFileMailboxInfo';

const initialTabConfig: TabScalingAndLayoutConfig = {
    controlsInOverflow: [],
    layout: [],
    removedDefaultGroups: [],
    showLabelsPreference: 1,
    staticGroupIdOrdering: [],
};

const initialTabsConfig: RibbonConfiguration = {
    drawTab: initialTabConfig,
    formatTab: initialTabConfig,
    folderTab: initialTabConfig,
    helpTab: initialTabConfig,
    homeTab: initialTabConfig,
    insertTab: initialTabConfig,
    messageTab: initialTabConfig,
    notesTab: initialTabConfig,
    optionsTab: initialTabConfig,
    pictureTab: initialTabConfig,
    readTab: initialTabConfig,
    sendReceiveTab: initialTabConfig,
    tableFormatTab: initialTabConfig,
    viewTab: initialTabConfig,
    searchToolsTab: initialTabConfig,
    mailMergeTab: initialTabConfig,
};

const initialRibbonConfig: MailRibbonConfigStore = {
    contextualTabs: [],
    /* the key here is the editorId, which is what we use to differentiate controls in multiple tabs as well */
    contextualTabsMultiWindows: new Map<string, MailRibbonTabId[]>(),
    multiline: initialTabsConfig,
    singleline: initialTabsConfig,
    version: 0,
};

export interface MailRibbonStoreMap {
    entries: Map<AccountKey, MailRibbonStore>;
    selectedTabMultiWindow: Map<Window, MailRibbonTabId | HelpRibbonTabId>;
}

export interface MailRibbonStore {
    hasLoaded: boolean;
    ribbonConfig: MailRibbonConfigStore;
}

export const getStore: () => MailRibbonStoreMap = createStore('MailRibbonConfiguration', {
    entries: new Map<AccountKey, MailRibbonStore>(),
    selectedTabMultiWindow: new Map<Window, MailRibbonTabId | HelpRibbonTabId>(),
});

const initialRibbonStore: MailRibbonStore = {
    hasLoaded: false,
    ribbonConfig: initialRibbonConfig,
};

export function getCurrentStore(mailboxInfo?: MailboxInfo): MailRibbonStore {
    const correctMailboxInfo =
        mailboxInfo &&
        !isUnifiedInboxMailboxInfo(mailboxInfo) &&
        !(isGlobalFeatureEnabled('mon-msg-local-parser') && isMessageFileMailboxInfo(mailboxInfo)) // Don't treat local .msg files as separate accounts
            ? mailboxInfo
            : getGlobalSettingsAccountMailboxInfo();

    const accountKey = getAccountKeyForMailboxInfo(correctMailboxInfo);
    const store = getStore();
    const currentStore = store.entries.get(accountKey);

    return (
        currentStore ||
        store.entries.set(accountKey, initialRibbonStore).get(accountKey) ||
        initialRibbonStore
    );
}

export function getMailRibbonConfigStore(mailboxInfo?: MailboxInfo): MailRibbonConfigStore {
    return getCurrentStore(mailboxInfo).ribbonConfig;
}

export function setSelectedTab(
    tabId: MailRibbonTabId | HelpRibbonTabId,
    targetWindow: Window = window
): void {
    setSelectedTabMultiWindow(tabId, targetWindow);
}

export function setSelectedTabMultiWindow(
    tabId: MailRibbonTabId | HelpRibbonTabId | undefined,
    targetWindow: Window
): void {
    const ribbonStoreMap = getStore();
    if (tabId) {
        ribbonStoreMap.selectedTabMultiWindow.set(targetWindow, tabId);
    } else {
        ribbonStoreMap.selectedTabMultiWindow.delete(targetWindow);
    }
}
